import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Workspace/brand scope, established by a route loader and read by every
 * screen below it (design doc §22.1).
 *
 * The context deliberately exposes only identifiers, not data: a screen that
 * wants the brand record asks TanStack Query for it under a key derived from
 * this scope, so there is exactly one cached copy.
 */
export interface WorkspaceScopeValue {
  readonly workspaceId: string;
  readonly brandId: string | null;
}

const WorkspaceScopeContext = createContext<WorkspaceScopeValue | null>(null);

export function WorkspaceScopeProvider({
  workspaceId,
  brandId,
  children,
}: {
  readonly workspaceId: string;
  readonly brandId: string | null;
  readonly children: ReactNode;
}) {
  const value = useMemo<WorkspaceScopeValue>(
    () => ({ workspaceId, brandId }),
    [workspaceId, brandId],
  );
  return <WorkspaceScopeContext value={value}>{children}</WorkspaceScopeContext>;
}

/**
 * @throws when used outside a workspace route — a screen that reads tenancy
 * from nowhere is exactly the bug CLAUDE.md §1.4 forbids on the server side,
 * and the client should not be sloppier than the server.
 */
export function useWorkspaceScope(): WorkspaceScopeValue {
  const value = useContext(WorkspaceScopeContext);
  if (!value) throw new Error('useWorkspaceScope must be used inside a workspace route.');
  return value;
}
